import { TextValidatorType } from "anketa-core/base";
import { Group, Section } from "anketa-core/group";
import { Translation } from "anketa-core/i18n";
import { OpenQuestion } from "anketa-core/open_question";
import { OptionsQuestion } from "anketa-core/options_question";
import { TextValidator } from "anketa-core/validation";

export const setupNetdataGroup = (netdataScanners: Section, idx = 0) => {
  const netdataGroup = netdataScanner(idx);
  netdataScanners.addChild(netdataGroup.netdataScanner);

  function onConfigChange() {
    const name = netdataGroup.netdataName.fact.raw;

    if (name && name.toString().trim() !== "") {
      netdataGroup.netdataName.removeChangeListener(onConfigChange);
      setupNetdataGroup(netdataScanners, idx + 1);
    }
  }

  netdataGroup.netdataName.addChangeListener(onConfigChange);
};

export const netdataScanner = (
  idx: number
): { netdataScanner: Group; netdataName: OpenQuestion } => {
  const idPrefix = `netdata_scanner_${idx}`;

  const netdataScanner = new Group(idPrefix, [
    new Translation("en", "Unconfigured Netdata Scanner"),
    new Translation("de", "Unkonfigurierter Netdata Scanner"),
  ]);

  const name = new OpenQuestion(`${idPrefix}_name`, [
    new Translation("en", "Display name"),
    new Translation("de", "Anzeigename"),
  ]);
  name.validator = new TextValidator();

  name.required = true;
  name.addChangeListener(() => {
    const plainName = name.fact.raw.toString();
    if (plainName.trim() !== "") {
      netdataScanner.title = [
        new Translation("en", plainName),
        new Translation("de", plainName),
      ];
    } else {
      netdataScanner.title = [
        new Translation("en", "Unconfigured Netdata Scanner"),
        new Translation("de", "Unkonfigurierter Netdata Scanner"),
      ];
    }
  });
  netdataScanner.addChild(name);

  const netdataSearchDomain = new OpenQuestion(`${idPrefix}_search_domain`, [
    new Translation("en", "Search Domain"),
    new Translation("de", "Suchdomäne"),
  ]);
  netdataSearchDomain.validator = new TextValidator();

  netdataSearchDomain.help = [
    new Translation(
      "en",
      "The domain to use for device name lookups or filtering (optional)."
    ),
    new Translation(
      "de",
      "Die Domäne, die für Gerätesuchen oder Filterung verwendet wird (optional)."
    ),
  ];
  netdataScanner.addChild(netdataSearchDomain);

  const netdataUrl = new OpenQuestion(`${idPrefix}_url`, [
    new Translation("en", "Netdata API URL"),
    new Translation("de", "Netdata API-URL"),
  ]);
  netdataUrl.validator = new TextValidator(TextValidatorType.Uri);
  netdataUrl.required = true;
  netdataUrl.help = [
    new Translation(
      "en",
      "The full URL to your Netdata instance's API (e.g., https://netdata.example.com:19999)."
    ),
    new Translation(
      "de",
      "Die vollständige URL zur API Ihrer Netdata-Instanz (z.B. https://netdata.beispiel.de:19999)."
    ),
  ];
  netdataScanner.addChild(netdataUrl);

  const netdataUsername = new OpenQuestion(`${idPrefix}_username`, [
    new Translation("en", "API Username"),
    new Translation("de", "API-Benutzername"),
  ]);
  netdataUsername.validator = new TextValidator();
  netdataUsername.help = [
    new Translation(
      "en",
      "The username for authenticating with the Netdata API (if required)."
    ),
    new Translation(
      "de",
      "Der Benutzername für die Authentifizierung an der Netdata API (falls erforderlich)."
    ),
  ];
  netdataScanner.addChild(netdataUsername);

  const netdataPassword = new OpenQuestion(`${idPrefix}_password`, [
    new Translation("en", "API Password"),
    new Translation("de", "API-Passwort"),
  ]);
  netdataPassword.readonly = true;
  netdataPassword.help = [
    new Translation(
      "en",
      "The password for authenticating with the Netdata API (if required)."
    ),
    new Translation(
      "de",
      "Das Passwort für die Authentifizierung an der Netdata API (falls erforderlich)."
    ),
  ];
  netdataScanner.addChild(netdataPassword);

  const netdataInsecure = new OptionsQuestion(
    `${idPrefix}_insecure`,
    [
      new Translation("en", "Disable Certificate Verification"),
      new Translation("de", "Zertifikatsüberprüfung deaktivieren"),
    ],
    "default.options.yes_no"
  );
  netdataInsecure.help = [
    new Translation(
      "en",
      "Should SSL/TLS certificate verification be disabled? Only use this for testing with self-signed certificates."
    ),
    new Translation(
      "de",
      "Soll die Überprüfung von SSL/TLS-Zertifikaten deaktiviert werden? Nur für Tests mit selbstsignierten Zertifikaten verwenden."
    ),
  ];
  if (netdataInsecure.fact.raw === null) {
    netdataInsecure.setFactValue(1);
  }
  netdataScanner.addChild(netdataInsecure);

  const netdataParallelism = new OptionsQuestion(
    `${idPrefix}_parallelism`,
    [
      new Translation("en", "Parallelism"),
      new Translation("de", "Parallelität"),
    ],
    "default.options.yes_no"
  );
  netdataParallelism.help = [
    new Translation(
      "en",
      "The number of concurrent API requests allowed (optional, advanced)."
    ),
    new Translation(
      "de",
      "Die Anzahl gleichzeitiger API-Anfragen (optional, fortgeschritten)."
    ),
  ];
  if (netdataParallelism.fact.raw === null) {
    netdataParallelism.setFactValue(1);
  }
  netdataScanner.addChild(netdataParallelism);

  return { netdataScanner: netdataScanner, netdataName: name };
};
